import { isValid, parseISO } from 'date-fns'
import { sortBy } from 'lodash/fp'

import { zipSeries } from '@/entities/bi'

export type DriverRow = { date: string; shares: Record<string, number> }

export type DemandDriversData = { rows: DriverRow[]; drivers: string[] }

export type DriverSummary = {
	topDriver: string | null
	latestShare: number | null
	change10d: number | null
	compareDate: string | null
}

export const CHANGE_WINDOW_POINTS = 10

const DATE_KEY = 'date_key'

const toNonNegative = (value: unknown): number => {
	const n = Number(value)

	return Number.isFinite(n) && n > 0 ? n : 0
}

export const buildDriverRows = (
	meta: string[],
	data: unknown[][]
): DemandDriversData => {
	const metrics = meta.filter((name) => name !== DATE_KEY)

	if (!metrics.length || !data.length) return { rows: [], drivers: [] }

	const validRaws = zipSeries<Record<string, unknown>>(meta, data).filter(
		(raw) => {
			const dateValue = raw[DATE_KEY]

			return typeof dateValue === 'string' && isValid(parseISO(dateValue))
		}
	)

	if (!validRaws.length) return { rows: [], drivers: [] }

	const rows = sortBy(
		'date',
		validRaws.map((raw) => {
			const values = metrics.map((name) => toNonNegative(raw[name]))
			const sum = values.reduce((acc, v) => acc + v, 0)
			const shares = Object.fromEntries(
				metrics.map((name, i) => [name, sum > 0 ? values[i] / sum : 0])
			)

			return { date: raw[DATE_KEY] as string, shares }
		})
	)

	const meanShare = (name: string) =>
		rows.reduce((acc, row) => acc + row.shares[name], 0) / rows.length
	const drivers = [...metrics].sort(
		(a, b) => meanShare(b) - meanShare(a) || a.localeCompare(b)
	)

	return { rows, drivers }
}

const EMPTY_SUMMARY: DriverSummary = {
	topDriver: null,
	latestShare: null,
	change10d: null,
	compareDate: null,
}

export const summarizeDrivers = (
	data: DemandDriversData | undefined
): DriverSummary => {
	if (!data?.rows.length || !data.drivers.length) return EMPTY_SUMMARY

	const latest = data.rows[data.rows.length - 1]
	const topDriver = data.drivers.reduce((best, name) =>
		latest.shares[name] > latest.shares[best] ? name : best
	)
	const compareIndex = data.rows.length - 1 - CHANGE_WINDOW_POINTS
	const compare = compareIndex >= 0 ? data.rows[compareIndex] : null

	return {
		topDriver,
		latestShare: latest.shares[topDriver],
		change10d: compare
			? (latest.shares[topDriver] - compare.shares[topDriver]) * 100
			: null,
		compareDate: compare?.date ?? null,
	}
}
